import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@app/shared/services/translate.service';
import { WebApplicationConfigurationService } from '@app/shared/services/web-application-configuration.service';
import { LangChangeEvent } from '@ngx-translate/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { forkJoin } from 'rxjs';
import { IdentityProviderService } from '../services/identity-provider.service';

@Injectable()
export class AuthServiceProvider {
  private oauthService = inject(OAuthService);
  private webApplicationConfigurationService = inject(WebApplicationConfigurationService);
  private identityProviderService = inject(IdentityProviderService);
  private router = inject(Router);
  private location = inject(Location);
  private translateService = inject(TranslateService);

  public load(): Promise<void> {
    return new Promise((resolve) => {
      const authCodeFlowConfig: AuthConfig = {};
      forkJoin([
        this.webApplicationConfigurationService.getPublicPropertyValue(this.identityProviderService.getConfigurationKey('revocationEndpoint'), undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue(this.identityProviderService.getConfigurationKey('tokenEndpoint'), undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue(this.identityProviderService.getConfigurationKey('issuer'), undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue(this.identityProviderService.getConfigurationKey('clientId'), undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue('public.authentication-config.responseType', undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue('public.authentication-config.showDebugInformation', undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue('public.authentication-config.timeoutFactor', undefined),
        this.webApplicationConfigurationService.getPublicPropertyValue('public.authentication-config.requireHttps', undefined)
      ]).subscribe(([revocationEndpoint, tokenEndpoint, issuer, clientId, responseType, showDebugInformation, timeoutFactor, requireHttps]) => {
        authCodeFlowConfig.issuer = issuer ? issuer : undefined;
        authCodeFlowConfig.clientId = clientId ? clientId : undefined;
        authCodeFlowConfig.revocationEndpoint = revocationEndpoint ? revocationEndpoint : undefined;
        authCodeFlowConfig.tokenEndpoint = tokenEndpoint ? tokenEndpoint : undefined;
        authCodeFlowConfig.redirectUri = this.getRedirectURI();
        authCodeFlowConfig.responseType = responseType ? responseType : undefined;
        authCodeFlowConfig.showDebugInformation = showDebugInformation == 'true';
        authCodeFlowConfig.timeoutFactor = timeoutFactor ? parseFloat(timeoutFactor) : undefined;
        authCodeFlowConfig.requireHttps = requireHttps === 'true';

        this.oauthService.configure(authCodeFlowConfig);
        this.oauthService.setupAutomaticSilentRefresh({}, 'access_token');

        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
          this.oauthService.customQueryParams = {
            ui_locales: event.lang
          };
        });

        resolve();
      });
    });
  }

  public getRedirectURI(): string {
    const idp = sessionStorage.getItem('idp') ? `?i=${sessionStorage.getItem('idp')}` : '';
    return window.location.origin + this.location.prepareExternalUrl(this.router.createUrlTree([`/home`]).toString()) + idp;
  }

  public getPostLogoutRedirectURI(): string {
    return window.location.origin + this.location.prepareExternalUrl(this.router.createUrlTree([`/public/login`]).toString());
  }
}
